// src/screens/PointSystem.jsx (or PointManagement.jsx)
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../css/Dashboard.css";
import "../css/Notifications.css"; // reuse same styles
import useIdleLogout from "../hooks/useIdleLogout";
// ====== ENV CONFIG ======
const POINT_API_BASE = (process.env.REACT_APP_POINT_SYSTEM_API_URL || "").replace(
  /\/+$/,
  ""
);

// Single global conversion rule endpoint
const POINT_CONV_API_BASE = (
  process.env.REACT_APP_POINT_CONVERSION_API_URL || ""
).replace(/\/+$/, "");

/* ========= helpers ========= */
function getAccessTokenOrThrow() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) {
      const err = new Error("Missing access token. Please login again.");
      err.status = 401;
      throw err;
    }
    return token;
  } catch {
    const err = new Error("Missing access token. Please login again.");
    err.status = 401;
    throw err;
  }
}

// Shared error alert + optional redirect on 401
function showErrorAndMaybeRedirect(nav, title, err) {
  const msg = err?.message || "Something went wrong. Please try again.";
  const status = err?.status;
  Swal.fire({
    icon: "error",
    title,
    text: msg,
  }).then(() => {
    if (status === 401) {
      nav("/login", { replace: true });
    }
  });
}

// Map backend payload → UI shape (Point System)
function normalizeRules(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, idx) => {
    const id =
      r.id ??
      r.point_system_id ??
      r.point_id ??
      r.rule_id ??
      idx + 1;

    return {
      id,
      min_amount_per_point: Number(
        r.min_amount_per_point ??
          r.minAmountPerPoint ??
          r.min_amount ??
          0
      ),
      point_to_award: Number(
        r.point_to_award ??
          r.points_to_award ??
          r.points ??
          0
      ),
      is_active:
        r.is_active ??
        r.active ??
        r.status ??
        true,
      createdAt:
        r.createdAt ??
        r.created_at ??
        r.created_at_ts ??
        null,
    };
  });
}

// Map backend payload → UI shape (Conversion)
function normalizeConversionResponse(data) {
  if (!data) return [];
  const src = data.data ?? data.rule ?? data;
  if (!src) return [];
  const arr = Array.isArray(src) ? src : [src];

  return arr.map((r, idx) => ({
    id: r.id ?? r.point_conversion_id ?? idx + 1,
    pointsRequired: Number(
      r.points_required ?? r.pointsRequired ?? r.points ?? 0
    ),
    walletAmount: Number(
      r.wallet_amount ?? r.walletAmount ?? r.amount ?? 0
    ),
    is_active: r.is_active ?? r.active ?? r.status ?? true,
    createdAt: r.created_at ?? r.createdAt ?? null,
  }));
}

export default function PointSystem() {
    useIdleLogout();
  const nav = useNavigate();

  // ---------- POINT SYSTEM (TOP CARD) ----------
  const [addMinAmount, setAddMinAmount] = useState("");
  const [addPointValue, setAddPointValue] = useState("");
  const [addIsActive, setAddIsActive] = useState(true);

  const [rules, setRules] = useState([]);

  const [editRowId, setEditRowId] = useState(null);
  const [editMinAmount, setEditMinAmount] = useState("");
  const [editPointValue, setEditPointValue] = useState("");
  const [editIsActive, setEditIsActive] = useState(true);

  const [loadingList, setLoadingList] = useState(false);
  const [savingAdd, setSavingAdd] = useState(false);
  const [savingEditId, setSavingEditId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ---------- POINT CONVERSION RULE (BOTTOM CARD) ----------
  const [convPoints, setConvPoints] = useState("");
  const [convAmount, setConvAmount] = useState("");
  const [convIsActive, setConvIsActive] = useState(true);

  const [convRules, setConvRules] = useState([]);
  const [convLoading, setConvLoading] = useState(false);

  const [convEditRowId, setConvEditRowId] = useState(null);
  const [convEditPoints, setConvEditPoints] = useState("");
  const [convEditAmount, setConvEditAmount] = useState("");
  const [convEditIsActive, setConvEditIsActive] = useState(true);
  const [convSavingAdd, setConvSavingAdd] = useState(false);
  const [convSavingRowId, setConvSavingRowId] = useState(null);
  const [convDeletingId, setConvDeletingId] = useState(null);

  // Layout helpers
  const wideEdge = { marginInline: "clamp(-16px, -2.5vw, -32px)" };
  const TOP_PAD = { paddingInline: "clamp(16px, 2.5vw, 32px)" };
  const ACTION_PILL_STYLE = { minWidth: 160 }; // 🔥 same width for Save + Cancel

  /* ================= POINT SYSTEM FETCH ================= */
  useEffect(() => {
    if (!POINT_API_BASE) {
      console.error("Missing REACT_APP_POINT_SYSTEM_API_URL");
      Swal.fire({
        icon: "error",
        title: "Point API not configured",
        text: "Please set REACT_APP_POINT_SYSTEM_API_URL in your .env file.",
      });
      return;
    }

    const fetchRules = async () => {
      setLoadingList(true);
      try {
        const token = getAccessTokenOrThrow();
        const res = await fetch(POINT_API_BASE, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          let msg = `Failed to load point rules (HTTP ${res.status})`;
          try {
            const data = await res.json();
            msg = data?.message || data?.error || msg;
          } catch {}
          const error = new Error(msg);
          error.status = res.status;
          throw error;
        }

        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : data?.data
          ? [data.data]
          : data
          ? [data]
          : [];
        setRules(normalizeRules(list));
      } catch (err) {
        console.error("[PointSystem] fetchRules error:", err);
        showErrorAndMaybeRedirect(nav, "Could not load rules", err);
      } finally {
        setLoadingList(false);
      }
    };

    fetchRules();
  }, [nav]);

  /* ================= CONVERSION RULE FETCH ================= */
  useEffect(() => {
    const refreshConversion = async () => {
      if (!POINT_CONV_API_BASE) return;
      setConvLoading(true);
      try {
        const token = getAccessTokenOrThrow();
        const res = await fetch(POINT_CONV_API_BASE, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          if (res.status === 404) {
            setConvRules([]);
            return;
          }
          let msg = `Failed to load conversion rule (HTTP ${res.status})`;
          try {
            const data = await res.json();
            msg = data?.message || data?.error || msg;
          } catch {}
          const error = new Error(msg);
          error.status = res.status;
          throw error;
        }

        const data = await res.json();
        const list = normalizeConversionResponse(data);
        setConvRules(list);
      } catch (err) {
        console.error("[PointSystem] fetch conversion error:", err);
        showErrorAndMaybeRedirect(
          nav,
          "Could not load conversion rules",
          err
        );
      } finally {
        setConvLoading(false);
      }
    };

    refreshConversion();
  }, [nav]);

  /* ================= POINT SYSTEM UPSERT ================= */
  async function upsertRule(id, body) {
    if (!POINT_API_BASE) {
      const err = new Error("Point API not configured in .env");
      err.status = 500;
      throw err;
    }

    try {
      const token = getAccessTokenOrThrow();
      const url = id ? `${POINT_API_BASE}/${id}` : POINT_API_BASE;
      const method = id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Failed to ${id ? "update" : "add"} rule (HTTP ${res.status})`;
        const error = new Error(msg);
        error.status = res.status;
        throw error;
      }

      let list = null;
      let updatedRule = null;

      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data?.data)) {
        list = data.data;
      } else if (data?.rule || data?.data?.rule) {
        updatedRule = normalizeRules([data.rule || data.data.rule])[0];
      }

      return { list, updatedRule };
    } catch (err) {
      if (err.status === 401) {
        nav("/login", { replace: true });
      }
      throw err;
    }
  }

  /* ================= POINT SYSTEM ADD ================= */
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!addMinAmount || Number(addMinAmount) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid minimum amount" });
      return;
    }
    if (!addPointValue || Number(addPointValue) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid points to award" });
      return;
    }

    const body = {
      min_amount_per_point: Number(addMinAmount),
      point_to_award: Number(addPointValue),
      is_active: Boolean(addIsActive),
    };

    setSavingAdd(true);
    try {
      const { list, updatedRule } = await upsertRule(null, body);

      if (list) {
        setRules(normalizeRules(list));
      } else if (updatedRule) {
        setRules((prev) => [updatedRule, ...prev]);
      } else {
        const localRule = {
          id: Date.now(),
          ...body,
          createdAt: new Date().toISOString(),
        };
        setRules((prev) => [localRule, ...prev]);
      }

      Swal.fire({
        icon: "success",
        title: "Point rule added!",
      });

      setAddMinAmount("");
      setAddPointValue("");
      setAddIsActive(true);
    } catch (err) {
      console.error("[PointSystem] add error:", err);
      showErrorAndMaybeRedirect(nav, "Unable to save rule", err);
    } finally {
      setSavingAdd(false);
    }
  };

  /* ================= POINT SYSTEM INLINE EDIT ================= */
  const handleStartEdit = (rule) => {
    setEditRowId(rule.id);
    setEditMinAmount(String(rule.min_amount_per_point));
    setEditPointValue(String(rule.point_to_award));
    setEditIsActive(Boolean(rule.is_active));
  };

  const handleSaveInline = async (row) => {
    if (!editMinAmount || Number(editMinAmount) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid minimum amount" });
      return;
    }
    if (!editPointValue || Number(editPointValue) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid points to award" });
      return;
    }

    const body = {
      min_amount_per_point: Number(editMinAmount),
      point_to_award: Number(editPointValue),
      is_active: Boolean(editIsActive),
    };

    setSavingEditId(row.id);
    try {
      const { list, updatedRule } = await upsertRule(row.id, body);

      if (list) {
        setRules(normalizeRules(list));
      } else if (updatedRule) {
        setRules((prev) =>
          prev.map((r) => (r.id === row.id ? updatedRule : r))
        );
      } else {
        const localRule = {
          id: row.id,
          ...body,
          createdAt: row.createdAt || new Date().toISOString(),
        };
        setRules((prev) =>
          prev.map((r) => (r.id === row.id ? localRule : r))
        );
      }

      Swal.fire({
        icon: "success",
        title: "Point rule updated!",
      });

      setEditRowId(null);
      setEditMinAmount("");
      setEditPointValue("");
      setEditIsActive(true);
    } catch (err) {
      console.error("[PointSystem] inline edit error:", err);
      showErrorAndMaybeRedirect(nav, "Unable to update rule", err);
    } finally {
      setSavingEditId(null);
    }
  };

  const handleCancelInline = () => {
    setEditRowId(null);
    setEditMinAmount("");
    setEditPointValue("");
    setEditIsActive(true);
  };

  /* ================= POINT SYSTEM DELETE ================= */
  const handleDelete = (id) => {
    if (!POINT_API_BASE) {
      Swal.fire({
        icon: "error",
        title: "Point API not configured",
      });
      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This will permanently delete the rule.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (resConfirm) => {
      if (!resConfirm.isConfirmed) return;

      setDeletingId(id);
      try {
        const token = getAccessTokenOrThrow();
        const res = await fetch(`${POINT_API_BASE}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        let data = {};
        try {
          data = await res.json();
        } catch {}

        if (!res.ok) {
          const msg =
            data?.message ||
            data?.error ||
            `Failed to delete rule (HTTP ${res.status})`;
          const error = new Error(msg);
          error.status = res.status;
          throw error;
        }

        let list = null;
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data?.data)) list = data.data;

        if (list) {
          setRules(normalizeRules(list));
        } else {
          setRules((prev) => prev.filter((r) => r.id !== id));
        }

        Swal.fire({ icon: "success", title: "Deleted!" });
      } catch (err) {
        console.error("[PointSystem] delete error:", err);
        showErrorAndMaybeRedirect(nav, "Unable to delete rule", err);
      } finally {
        setDeletingId(null);
      }
    });
  };

  /* ================= UX HELPERS ================= */
  const currentSummary = useMemo(() => {
    if (!addMinAmount || !addPointValue) return "";
    const m = Number(addMinAmount);
    const p = Number(addPointValue);
    if (!m || !p) return "";
    return `For every Nu. ${m.toLocaleString()} spent, award ${p.toLocaleString()} point${
      p > 1 ? "s" : ""
    }.`;
  }, [addMinAmount, addPointValue]);

  const conversionSummary = useMemo(() => {
    if (!convPoints || !convAmount) return "";
    const pts = Number(convPoints);
    const amt = Number(convAmount);
    if (!pts || !amt) return "";
    return `${pts.toLocaleString()} point${
      pts > 1 ? "s" : ""
    } = Nu. ${amt.toLocaleString()} redemption.`;
  }, [convPoints, convAmount]);

  /* ================= CONVERSION ADD (TOP FORM ONLY, POST) ================= */
  const handleSaveConversionAdd = async (e) => {
    e.preventDefault();

    if (!convPoints || Number(convPoints) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid points" });
      return;
    }
    if (!convAmount || Number(convAmount) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid amount in Nu." });
      return;
    }

    if (!POINT_CONV_API_BASE) {
      Swal.fire({
        icon: "error",
        title: "Point Conversion API not configured",
      });
      return;
    }

    const body = {
      points_required: Number(convPoints),
      wallet_amount: Number(convAmount),
      is_active: Boolean(convIsActive),
    };

    setConvSavingAdd(true);
    try {
      const token = getAccessTokenOrThrow();
      const res = await fetch(POINT_CONV_API_BASE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Failed to save conversion rule (HTTP ${res.status})`;
        const error = new Error(msg);
        error.status = res.status;
        throw error;
      }

      const list = normalizeConversionResponse(data);
      setConvRules(list);

      Swal.fire({
        icon: "success",
        title: "Conversion rule saved!",
      });

      setConvPoints("");
      setConvAmount("");
      setConvIsActive(true);
    } catch (err) {
      console.error("[PointSystem] save conversion error:", err);
      showErrorAndMaybeRedirect(
        nav,
        "Unable to save conversion rule",
        err
      );
    } finally {
      setConvSavingAdd(false);
    }
  };

  /* ================= CONVERSION INLINE EDIT (TABLE ROW, PUT) ================= */
  const handleStartEditConversion = (rule) => {
    setConvEditRowId(rule.id);
    setConvEditPoints(String(rule.pointsRequired));
    setConvEditAmount(String(rule.walletAmount));
    setConvEditIsActive(Boolean(rule.is_active));
  };

  const handleSaveConversionInline = async (row) => {
    if (!convEditPoints || Number(convEditPoints) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid points" });
      return;
    }
    if (!convEditAmount || Number(convEditAmount) <= 0) {
      Swal.fire({ icon: "error", title: "Enter valid amount in Nu." });
      return;
    }

    if (!POINT_CONV_API_BASE) {
      Swal.fire({
        icon: "error",
        title: "Point Conversion API not configured",
      });
      return;
    }

    const body = {
      points_required: Number(convEditPoints),
      wallet_amount: Number(convEditAmount),
      is_active: Boolean(convEditIsActive),
    };

    setConvSavingRowId(row.id);
    try {
      const token = getAccessTokenOrThrow();
      const res = await fetch(POINT_CONV_API_BASE, {
        method: "PUT", // edit uses PUT
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Failed to update conversion rule (HTTP ${res.status})`;
        const error = new Error(msg);
        error.status = res.status;
        throw error;
      }

      const list = normalizeConversionResponse(data);
      if (list.length) {
        setConvRules(list);
      } else {
        const updated = {
          ...row,
          pointsRequired: body.points_required,
          walletAmount: body.wallet_amount,
          is_active: body.is_active,
        };
        setConvRules((prev) =>
          prev.map((r) => (r.id === row.id ? updated : r))
        );
      }

      Swal.fire({
        icon: "success",
        title: "Conversion rule updated!",
      });

      setConvEditRowId(null);
      setConvEditPoints("");
      setConvEditAmount("");
      setConvEditIsActive(true);
    } catch (err) {
      console.error("[PointSystem] inline conversion edit error:", err);
      showErrorAndMaybeRedirect(
        nav,
        "Unable to update conversion rule",
        err
      );
    } finally {
      setConvSavingRowId(null);
    }
  };

  const handleCancelConversionInline = () => {
    setConvEditRowId(null);
    setConvEditPoints("");
    setConvEditAmount("");
    setConvEditIsActive(true);
  };

  /* ================= CONVERSION DELETE ================= */
  const handleDeleteConversion = (id) => {
    if (!POINT_CONV_API_BASE) {
      Swal.fire({
        icon: "error",
        title: "Point Conversion API not configured",
      });
      return;
    }

    Swal.fire({
      icon: "warning",
      title: "Delete conversion rule?",
      text: "This will remove the conversion rule.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async (resConfirm) => {
      if (!resConfirm.isConfirmed) return;

      setConvDeletingId(id);
      try {
        const token = getAccessTokenOrThrow();
        const res = await fetch(POINT_CONV_API_BASE, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const msg =
            data?.message ||
            data?.error ||
            `Failed to delete conversion rule (HTTP ${res.status})`;
          const error = new Error(msg);
          error.status = res.status;
          throw error;
        }

        setConvRules([]);
        setConvEditRowId(null);
        setConvEditPoints("");
        setConvEditAmount("");
        setConvEditIsActive(true);

        Swal.fire({ icon: "success", title: "Deleted!" });
      } catch (err) {
        console.error("[PointSystem] delete conversion error:", err);
        showErrorAndMaybeRedirect(
          nav,
          "Unable to delete conversion rule",
          err
        );
      } finally {
        setConvDeletingId(null);
      }
    });
  };

  /* ================= RENDER ================= */
  return (
    <div className="p-4 flex-grow-1">
      {/* ---------- POINT SYSTEM CARD ---------- */}
      <div className="card p-4 system-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 className="section-title mb-1">Point System</h4>
            <p className="text-muted mb-0">
              <b>Only one Point Rule should be active</b>
            </p>
          </div>
        </div>

        {/* ADD FORM */}
        <form onSubmit={handleAddSubmit} className="settings-form mt-3">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                Min Amount Per Point (Nu.)
              </label>
              <input
                type="number"
                className="form-control"
                value={addMinAmount}
                onChange={(e) => setAddMinAmount(e.target.value)}
                min="0"
                step="0.01"
                placeholder="e.g. 100"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">
                Point to Be Awarded
              </label>
              <input
                type="number"
                className="form-control"
                value={addPointValue}
                onChange={(e) => setAddPointValue(e.target.value)}
                min="0"
                step="1"
                placeholder="e.g. 1"
              />
            </div>

            <div className="col-md-4 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="add-is-active"
                  className="form-check-input"
                  checked={addIsActive}
                  onChange={() => setAddIsActive((v) => !v)}
                />
                <label
                  className="form-check-label"
                  htmlFor="add-is-active"
                >
                  Active
                </label>
              </div>
            </div>
          </div>

          {currentSummary && (
            <p className="text-muted small mt-2">
              <i className="bi bi-info-circle me-1" />
              {currentSummary}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mt-3"
            disabled={savingAdd}
          >
            {savingAdd ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              "Add Rule"
            )}
          </button>
        </form>

        {/* POINT RULES TABLE */}
        <div className="mt-4" style={wideEdge}>
          <div
            className="d-flex align-items-center justify-content-between mb-2"
            style={TOP_PAD}
          >
            <h5 className="mb-0">Point Rules</h5>
            {loadingList && (
              <small className="text-muted">
                <span className="spinner-border spinner-border-sm me-1" />
                Loading...
              </small>
            )}
          </div>

          <div
            className="table-responsive contrast-surface"
            style={TOP_PAD}
          >
            <table className="table table-sm align-middle contrast-table w-100">
              <thead>
                <tr>
                  <th>SI.No</th>
                  <th>Min Amount (Nu.)</th>
                  <th>Point to Be Awarded</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th style={{ width: 260 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {rules.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-muted text-center py-3"
                    >
                      {loadingList
                        ? "Loading rules..."
                        : "No rules added yet."}
                    </td>
                  </tr>
                ) : (
                  rules.map((r, idx) => {
                    const isEditing = r.id === editRowId;
                    const isSavingThisRow = savingEditId === r.id;
                    const isDeletingThisRow = deletingId === r.id;

                    return (
                      <tr key={r.id}>
                        <td>{idx + 1}</td>

                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={editMinAmount}
                              onChange={(e) =>
                                setEditMinAmount(e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            <>
                              Nu.{" "}
                              {r.min_amount_per_point.toLocaleString()}
                            </>
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={editPointValue}
                              onChange={(e) =>
                                setEditPointValue(e.target.value)
                              }
                              min="0"
                              step="1"
                            />
                          ) : (
                            r.point_to_award
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <div className="form-check">
                              <input
                                type="checkbox"
                                id={`edit-active-${r.id}`}
                                className="form-check-input"
                                checked={editIsActive}
                                onChange={() =>
                                  setEditIsActive((v) => !v)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`edit-active-${r.id}`}
                              >
                                Active
                              </label>
                            </div>
                          ) : r.is_active ? (
                            <span className="badge bg-success">
                              Active
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              Inactive
                            </span>
                          )}
                        </td>

                        <td>
                          <small className="text-muted">
                            {r.createdAt
                              ? new Date(r.createdAt).toLocaleString()
                              : "-"}
                          </small>
                        </td>

                        <td>
                          {isEditing ? (
                            <div className="action-buttons d-flex gap-3 justify-content-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                style={ACTION_PILL_STYLE}
                                onClick={() => handleSaveInline(r)}
                                disabled={
                                  isSavingThisRow || isDeletingThisRow
                                }
                              >
                                {isSavingThisRow ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save Rule"
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={ACTION_PILL_STYLE}
                                onClick={handleCancelInline}
                                disabled={isSavingThisRow}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="icon-btn icon-btn--edit"
                                onClick={() => handleStartEdit(r)}
                                disabled={
                                  isDeletingThisRow || savingEditId !== null
                                }
                                title="Edit"
                              >
                                <i className="bi bi-pencil" />
                              </button>

                              <button
                                type="button"
                                className="icon-btn icon-btn--delete"
                                onClick={() => handleDelete(r.id)}
                                disabled={
                                  isDeletingThisRow || savingEditId !== null
                                }
                                title="Delete"
                              >
                                {isDeletingThisRow ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="bi bi-trash" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ---------- POINT CONVERSION RULE CARD ---------- */}
      <div className="card p-4 system-card mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 className="section-title mb-1">Point Conversion Rule</h4>
            <p className="text-muted mb-0">
             <b> Define how accumulated points can be converted back to Nu. during redemption.</b>
            </p>
          </div>
        </div>

        {/* ADD CONVERSION FORM (top) */}
        <form onSubmit={handleSaveConversionAdd} className="settings-form mt-3">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Points</label>
              <input
                type="number"
                className="form-control"
                value={convPoints}
                onChange={(e) => setConvPoints(e.target.value)}
                min="0"
                step="1"
                placeholder="e.g. 100"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Equivalent Amount (Nu.)</label>
              <input
                type="number"
                className="form-control"
                value={convAmount}
                onChange={(e) => setConvAmount(e.target.value)}
                min="0"
                step="0.01"
                placeholder="e.g. 10"
              />
            </div>
            <div className="col-md-4 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="conv-is-active"
                  className="form-check-input"
                  checked={convIsActive}
                  onChange={() => setConvIsActive((v) => !v)}
                />
                <label
                  className="form-check-label"
                  htmlFor="conv-is-active"
                >
                  Active
                </label>
              </div>
            </div>
          </div>

          {conversionSummary && (
            <p className="text-muted small mt-2">
              <i className="bi bi-info-circle me-1" />
              {conversionSummary}
            </p>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mt-3"
            disabled={convSavingAdd}
          >
            {convSavingAdd ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              "Save Conversion Rule"
            )}
          </button>
        </form>

        {/* CONVERSION TABLE */}
        <div className="mt-4" style={wideEdge}>
          <div
            className="d-flex align-items-center justify-content-between mb-2"
            style={TOP_PAD}
          >
            <h5 className="mb-0">Conversion Rules</h5>
            {convLoading && (
              <small className="text-muted">
                <span className="spinner-border spinner-border-sm me-1" />
                Loading...
              </small>
            )}
          </div>

          <div
            className="table-responsive contrast-surface"
            style={TOP_PAD}
          >
            <table className="table table-sm align-middle contrast-table w-100">
              <thead>
                <tr>
                  <th>SI.No</th>
                  <th>Points</th>
                  <th>Equivalent Amount (Nu.)</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th style={{ width: 260 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {convRules.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-muted text-center py-3"
                    >
                      {convLoading
                        ? "Loading conversion rules..."
                        : "No conversion rules added yet."}
                    </td>
                  </tr>
                ) : (
                  convRules.map((r, idx) => {
                    const isEditing = r.id === convEditRowId;
                    const isSavingThisRow = convSavingRowId === r.id;
                    const isDeletingThisRow = convDeletingId === r.id;

                    return (
                      <tr key={r.id}>
                        <td>{idx + 1}</td>

                        {/* Points */}
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={convEditPoints}
                              onChange={(e) =>
                                setConvEditPoints(e.target.value)
                              }
                              min="0"
                              step="1"
                            />
                          ) : (
                            r.pointsRequired.toLocaleString()
                          )}
                        </td>

                        {/* Amount */}
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={convEditAmount}
                              onChange={(e) =>
                                setConvEditAmount(e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            <>
                              Nu.{" "}
                              {r.walletAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </>
                          )}
                        </td>

                        {/* Status */}
                        <td>
                          {isEditing ? (
                            <div className="form-check">
                              <input
                                type="checkbox"
                                id={`conv-edit-active-${r.id}`}
                                className="form-check-input"
                                checked={convEditIsActive}
                                onChange={() =>
                                  setConvEditIsActive((v) => !v)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`conv-edit-active-${r.id}`}
                              >
                                Active
                              </label>
                            </div>
                          ) : r.is_active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* Created */}
                        <td>
                          <small className="text-muted">
                            {r.createdAt
                              ? new Date(r.createdAt).toLocaleString()
                              : "-"}
                          </small>
                        </td>

                        {/* Actions */}
                        <td>
                          {isEditing ? (
                            <div className="action-buttons d-flex gap-3 justify-content-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                style={ACTION_PILL_STYLE}
                                onClick={() =>
                                  handleSaveConversionInline(r)
                                }
                                disabled={
                                  isSavingThisRow || isDeletingThisRow
                                }
                              >
                                {isSavingThisRow ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save Conversion"
                                )}
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={ACTION_PILL_STYLE}
                                onClick={handleCancelConversionInline}
                                disabled={isSavingThisRow}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="icon-btn icon-btn--edit"
                                title="Edit"
                                onClick={() =>
                                  handleStartEditConversion(r)
                                }
                                disabled={isDeletingThisRow}
                              >
                                <i className="bi bi-pencil" />
                              </button>
                              <button
                                type="button"
                                className="icon-btn icon-btn--delete"
                                title="Delete"
                                onClick={() =>
                                  handleDeleteConversion(r.id)
                                }
                                disabled={isDeletingThisRow}
                              >
                                {isDeletingThisRow ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="bi bi-trash" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()}{" "}
          <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
